import { css } from '@emotion/react';
import {
  computeUpgradeInfo,
  totalProductionCounters,
} from '@mythos/game/AIUtilities';
import {
  AbilityOutput,
  getEndOfGameCardAbility,
  upgradeConditionMet,
} from '@mythos/game/CardAbilities';
import { CardAffinity } from '@mythos/game/CardTypes';
import { GamePlayerViewModel, GameViewModel } from '@mythos/game/GameViewModel';
import * as Rules from '@mythos/game/Rules';
import {
  computeBaseCounters,
  getMaxCounters,
  makeContextFromGame,
} from '@mythos/game/Rules';
import { nonNull } from '@mythos/utils/utils';
import invariant from 'invariant';
import { times } from 'lodash';
import nullthrows from 'nullthrows';
import React, { useState } from 'react';
import { CardCountsView } from '../CardCountsView';
import { AffinityOrTypeCharToProphecySymbolImage } from '../CardHelpers';
import { CardEffect, UpgradeCondition } from '../CardView';
import { UIColorPalette } from '../ColorPalette';
import { UserWithColor } from '../GraphQLHelpers';
import PlayerBoardView from '../PlayerBoardView';
import Symbols, {
  BaseResourceToSymbol,
  ResourceToSymbol,
  SymbolView,
} from '../Symbols';
import { useLongPress } from './MButton';
import { MStyles } from './MStyles';
import { MModal, MSymbolPrefix } from './MUIComponents';

export default function MPlayerSummaryView(props: {
  player: GamePlayerViewModel;
  game: GameViewModel;
  user: UserWithColor;
  ready: boolean;
  isWinning: boolean;
  isSessionPlayer: boolean;

  favorDetails: FavorDetails;

  onShortPress?: () => void;
  onLongPress?: () => void;
  onLongPressEnd?: () => void;
}) {
  const { player, game, isSessionPlayer, isWinning, ready } = props;

  const { cardFavor, tributeFavor, prophecyFavor, totalFavor } =
    props.favorDetails;

  const longPressProps = useLongPress(props);

  const context = makeContextFromGame(player, game);
  const base_counts = computeBaseCounters(context);
  const production_counts = totalProductionCounters(context);
  const { maxGold, maxMilitary } = getMaxCounters(context);

  const profile_picture = (
    <img
      src={props.user.profilePictureURL}
      css={MPlayerSummaryViewStyles.profileSymbol}
    />
  );

  return (
    <div css={MPlayerSummaryViewStyles.playerInfoContainer} {...longPressProps}>
      <div css={MPlayerSummaryViewStyles.playerInfoLeft}>
        <div
          css={[
            MPlayerSummaryViewStyles.readySymbol,
            ready
              ? MPlayerSummaryViewStyles.readySymbolReady
              : MPlayerSummaryViewStyles.readySymbolNotReady,
          ]}
        />
        {profile_picture}
      </div>
      <div
        css={MPlayerSummaryViewStyles.name}
        style={{
          color: props.user.color,
        }}
      >
        {props.user.name}
      </div>
      <div
        css={MPlayerSummaryViewStyles.favorContainer}
        style={
          (isWinning && {
            backgroundColor: UIColorPalette.playerSummaryWinnerBG,
            borderRadius: 4,
          }) ||
          undefined
        }
      >
        {totalFavor}
        <SymbolView
          symbol={Symbols.FAVOR}
          css={MPlayerSummaryViewStyles.favorSymbol}
        />
      </div>
      <MPlayerResourceCounter
        resource="gold"
        count={player.counters.gold}
        baseCount={base_counts.gold}
        productionCount={production_counts.gold}
        maxCount={maxGold}
      />
      <MPlayerResourceCounter
        resource="military"
        count={player.counters.military}
        baseCount={base_counts.military}
        productionCount={production_counts.military}
        maxCount={maxMilitary}
      />
      <CardCountsView
        css={MPlayerSummaryViewStyles.cardCounts}
        player={player}
        game={game}
        singleRow={true}
      />
    </div>
  );
}

function MPlayerResourceCounter(props: {
  resource: 'gold' | 'military';
  count: number;
  baseCount: number;
  productionCount: number;
  maxCount: number;
}) {
  const baseResourceSymbol = nullthrows(BaseResourceToSymbol[props.resource]);
  const isMaxed = props.count >= props.maxCount;
  const isNearMax = props.count === props.maxCount - 1;
  return (
    <div css={MPlayerSummaryViewStyles.resourceContainer}>
      <div css={MPlayerSummaryViewStyles.resourceCount}>
        <span
          css={[
            MPlayerSummaryViewStyles.resourceCount,
            isMaxed && MPlayerSummaryViewStyles.maxedCountText,
            isNearMax && MPlayerSummaryViewStyles.nearMaxCountText,
          ]}
        >
          {props.count + props.baseCount}
        </span>
      </div>
      <div css={MPlayerSummaryViewStyles.resourceSymbolContainer}>
        <SymbolView
          symbol={ResourceToSymbol[props.resource]}
          css={MPlayerSummaryViewStyles.resourceSymbol}
        />
        <SymbolView
          symbol={baseResourceSymbol}
          css={MPlayerSummaryViewStyles.baseSymbol}
        />
      </div>
      <div css={MPlayerSummaryViewStyles.resourceCountDetailsContainer}>
        <div css={MPlayerSummaryViewStyles.productionCount}>
          <SymbolView
            symbol={baseResourceSymbol}
            css={MPlayerSummaryViewStyles.productionSymbol}
          />
          {props.baseCount}
        </div>
        <div css={MPlayerSummaryViewStyles.productionCount}>
          <SymbolView
            symbol={Symbols.EFFECT_PRODUCTION}
            css={MPlayerSummaryViewStyles.productionSymbol}
          />
          {props.productionCount}
        </div>
      </div>
    </div>
  );
}

const MPlayerSummaryViewStyles = {
  playerInfoContainer: css(
    {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,

      paddingRight: 4,

      backgroundColor: UIColorPalette.mPlayerInfoBG,
      ':nth-child(even)': {
        backgroundColor: UIColorPalette.mPlayerInfoBG2,
      },

      height: 32,
    },
    MStyles.touchable,
  ),
  playerInfoRow: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  }),
  playerInfoLeft: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',

    flexGrow: 0,
    flexShrink: 0,
  }),
  profileSymbol: css({
    height: 26,
    width: 26,
  }),
  readySymbol: css({
    height: 26,
    width: 4,
    borderRadius: 0,

    flexShrink: 0,
  }),
  readySymbolReady: css({
    backgroundColor: UIColorPalette.playerSummaryStatusReady,
  }),
  readySymbolNotReady: css({
    backgroundColor: UIColorPalette.playerSummaryStatusNotReady,
  }),
  name: css({
    fontSize: 14,
    fontWeight: 'bold',
    flexGrow: 1,
    flexShrink: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 100,
  }),
  favorContainer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 2,

    alignSelf: 'center',
    paddingTop: 4,
    paddingBottom: 4,

    width: 40,
    minWidth: 40,
    flexGrow: 0,
    flexShrink: 0,

    fontSize: 14,
  }),
  favorSymbol: css({
    width: 14,
  }),

  resourceContainer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'stretch',

    gap: 2,
    flexShrink: 0,
  }),
  resourceCount: css({
    textAlign: 'end',
    verticalAlign: 'middle',
    fontSize: 14,
    width: 18,
  }),
  maxedCountText: css({
    color: UIColorPalette.playerSummaryResourceTextMaxed,
  }),
  nearMaxCountText: css({
    color: UIColorPalette.playerSummaryResourceTextNearMaxed,
  }),
  resourceSymbolContainer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'end',
    justifyContent: 'start',
    flexShrink: 0,
  }),
  resourceSymbol: css({
    height: 14,
    width: 14,
  }),
  baseSymbol: css({
    marginLeft: -5,
    height: 9,
    width: 9,
  }),
  resourceCountDetailsContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    justifyContent: 'start',
    gap: 2,
    width: 22,
  }),
  productionSymbol: css({
    height: 10,
    width: 10,
  }),
  productionCount: css({
    fontSize: 10,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'start',
    gap: 1,
  }),
  time: css({
    fontSize: 12,
  }),
  cardCounts: css({
    marginLeft: 1,
  }),
};

const MPlayerDetailsView = (props: {
  player: GamePlayerViewModel;
  game: GameViewModel;
  user: UserWithColor;

  favorDetails: FavorDetails;
}) => {
  const { player, game, user } = props;
  const { cardFavor, tributeFavor, prophecyFavor, totalFavor } =
    props.favorDetails;

  const context = makeContextFromGame(player, game);

  return (
    <div css={MPlayerDetailsStyles.container}>
      <div css={MPlayerDetailsStyles.counterInfoSection}>
        <div css={MPlayerDetailsStyles.favorSection}>
          <MSymbolPrefix
            symbol={Symbols.FAVOR}
            symbolCss={MPlayerDetailsStyles.favorSymbol}
          >
            {totalFavor}
          </MSymbolPrefix>
        </div>
        <MPlayerDetailsResourceSection player={player} game={game} />
        <CardCountsView player={player} game={game} />
      </div>
      <MPlayerFavorBreakdownView favorDetails={props.favorDetails} />
      <MPropheciesView player={player} game={game} />
      <MAvailableUpgradesView player={player} game={game} />
      <PlayerBoardView player={player} game={game} user={user} />
    </div>
  );
};

const MPlayerDetailsResourceSection = (props: {
  player: GamePlayerViewModel;
  game: GameViewModel;
}) => {
  const { player, game } = props;

  const context = makeContextFromGame(player, game);
  const base_counts = computeBaseCounters(context);
  const production_counts = totalProductionCounters(context);
  return (
    <div css={MPlayerDetailsStyles.resourceSection}>
      <MSymbolPrefix
        symbol={ResourceToSymbol.gold}
        symbolCss={MPlayerDetailsStyles.resourceSymbol}
      >
        {player.counters.gold}
      </MSymbolPrefix>
      <MSymbolPrefix
        symbol={BaseResourceToSymbol.gold!}
        symbolCss={MPlayerDetailsStyles.resourceSymbol}
      >
        {base_counts.gold}
      </MSymbolPrefix>
      <MSymbolPrefix
        symbol={Symbols.EFFECT_PRODUCTION}
        symbolCss={MPlayerDetailsStyles.productionSymbol}
      >
        {production_counts.gold}
      </MSymbolPrefix>
      <MSymbolPrefix
        symbol={ResourceToSymbol.military}
        symbolCss={MPlayerDetailsStyles.resourceSymbol}
      >
        {player.counters.military}
      </MSymbolPrefix>
      <MSymbolPrefix
        symbol={BaseResourceToSymbol.military!}
        symbolCss={MPlayerDetailsStyles.resourceSymbol}
      >
        {base_counts.military}
      </MSymbolPrefix>
      <MSymbolPrefix
        symbol={Symbols.EFFECT_PRODUCTION}
        symbolCss={MPlayerDetailsStyles.productionSymbol}
      >
        {production_counts.military}
      </MSymbolPrefix>
    </div>
  );
};
const MPlayerDetailsTitleView = (props: { user: UserWithColor }) => {
  const { user } = props;

  return (
    <div
      css={MPlayerDetailsStyles.titleContainer}
      style={{
        color: user.color,
      }}
    >
      <img
        src={user.profilePictureURL}
        css={MPlayerDetailsStyles.profileSymbol}
      />
      {user.name}
    </div>
  );
};

const MPlayerDetailsStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'stretch',

    backgroundColor: UIColorPalette.mPlayerDetailsBG,
  }),
  counterInfoSection: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,

    padding: 2,
    paddingLeft: 4,
    paddingRight: 4,
    zoom: 1.5,

    backgroundColor: UIColorPalette.mPlayerDetailsBG,
  }),
  favorSection: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    fontSize: 20,
  }),
  favorSymbol: css({
    height: 20,
    width: 20,
  }),
  resourceSection: css({
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    gridTemplateRows: '1fr 1fr',
    gap: 4,
  }),
  resourceCounter: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  }),
  resourceSymbol: css({
    height: 16,
    width: 16,
  }),
  productionSymbol: css({
    height: 12,
    width: 12,
  }),
  titleContainer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  }),
  profileSymbol: css({
    height: 44,
    width: 44,
  }),
};

type FavorDetails = {
  cardFavor: number;
  tributeFavor: number;
  prophecyFavor: number;
  totalFavor: number;
};

export function MPlayerSummaryViews(props: {
  game: GameViewModel;
  players: GamePlayerViewModel[];
  sessionPlayerID?: string;
  userByID: Map<string, UserWithColor>;
  readyByUserID: { [k: string]: boolean };
}) {
  const { game, players, sessionPlayerID, userByID, readyByUserID } = props;

  const [shownPlayerID, setShownPlayerID] = useState<string | null>(null);

  const session_player_index = players.findIndex((player) => {
    return player.userID === sessionPlayerID;
  });
  const sorted_players =
    session_player_index !== -1
      ? [
          ...players.slice(session_player_index),
          ...players.slice(0, session_player_index),
        ]
      : players;

  const modalPlayer = game.playerStates.find(
    (player) => player.userID === shownPlayerID,
  );

  const favor_details_by_player_id = new Map(
    players.map((player) => {
      const context = Rules.makeContextFromGame(player, game);
      const { cardFavor, tributeFavor, prophecyFavor } =
        Rules.computePlayerFavorDetails(player, context);
      return [
        player.userID,
        {
          cardFavor,
          tributeFavor,
          prophecyFavor,
          totalFavor: cardFavor + tributeFavor + prophecyFavor,
        },
      ];
    }),
  );

  const max_total_favor = Math.max(
    ...Array.from(favor_details_by_player_id.values()).map(
      (details) => details.totalFavor,
    ),
  );

  return (
    <div css={MPlayerSummaryViewsStyles.container}>
      {sorted_players.map((player, i) => {
        const player_favor_details = favor_details_by_player_id.get(
          player.userID,
        )!;
        return (
          <MPlayerSummaryView
            key={player.userID}
            game={game}
            player={player}
            user={userByID.get(player.userID)!}
            ready={readyByUserID[player.userID]}
            isWinning={
              max_total_favor === player_favor_details.totalFavor &&
              max_total_favor > 0
            }
            isSessionPlayer={player.userID === sessionPlayerID}
            favorDetails={player_favor_details}
            onShortPress={() => {
              setShownPlayerID(player.userID);
            }}
            onLongPress={() => {
              setShownPlayerID(player.userID);
            }}
            onLongPressEnd={() => {
              setShownPlayerID(null);
            }}
          />
        );
      })}
      {modalPlayer && (
        <MModal
          title={
            <MPlayerDetailsTitleView user={userByID.get(modalPlayer.userID)!} />
          }
          show={modalPlayer !== null}
          onClose={() => {
            setShownPlayerID(null);
          }}
        >
          <MPlayerDetailsView
            player={modalPlayer}
            game={game}
            user={userByID.get(modalPlayer.userID)!}
            favorDetails={favor_details_by_player_id.get(modalPlayer.userID)!}
          />
        </MModal>
      )}
    </div>
  );
}

const MPlayerDetailsSectionWithTitle = (props: {
  title: string;
  children?: React.ReactNode;
}) => {
  const { title, children } = props;
  return (
    <div css={MPlayerDetailsSectionWithTitleStyles.sectionContainer}>
      <div css={MPlayerDetailsSectionWithTitleStyles.sectionTitle}>{title}</div>
      {children}
    </div>
  );
};
const MPlayerDetailsSectionWithTitleStyles = {
  sectionContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 4,

    padding: 4,
    borderRadius: 4,
    margin: 4,

    backgroundColor: UIColorPalette.mPlayerDetailsSectionBG,
  }),
  sectionTitle: css({
    fontSize: 18,
    textAlign: 'center',

    color: UIColorPalette.mPlayerSummaryText,
  }),
};

const MPlayerSummaryViewsStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  }),
};

const MPlayerFavorBreakdownView = (props: { favorDetails: FavorDetails }) => {
  const { tributeFavor, cardFavor, prophecyFavor } = props.favorDetails;

  return (
    <MPlayerDetailsSectionWithTitle title="Favor Breakdown">
      <div css={MExpandedPlayerSummaryStyles.favorDetailSection}>
        <MSymbolPrefix
          css={MExpandedPlayerSummaryStyles.favorDetailText}
          symbol={Symbols.TOKEN_TRIBUTE}
          symbolCss={MExpandedPlayerSummaryStyles.favorDetailSymbol}
        >
          {tributeFavor}
        </MSymbolPrefix>
        <MSymbolPrefix
          css={MExpandedPlayerSummaryStyles.favorDetailText}
          symbol={Symbols.CARD_BACK}
          symbolCss={MExpandedPlayerSummaryStyles.favorDetailSymbol}
        >
          {cardFavor}
        </MSymbolPrefix>
        <MSymbolPrefix
          css={MExpandedPlayerSummaryStyles.favorDetailText}
          symbol={Symbols.EOG}
          symbolCss={MExpandedPlayerSummaryStyles.favorDetailSymbol}
        >
          {prophecyFavor}
        </MSymbolPrefix>
      </div>
    </MPlayerDetailsSectionWithTitle>
  );
};

const MPropheciesView = (props: {
  player: GamePlayerViewModel;
  game: GameViewModel;
}) => {
  const { player, game } = props;

  const prophecies = player.cards
    .map((card) => getEndOfGameCardAbility(card))
    .filter(nonNull);

  const context = makeContextFromGame(player, game);
  const prophecy_outputs = prophecies
    .flatMap((prophecy) => {
      return prophecy.outputs.map((output) => {
        if (
          output.upgradeCondition &&
          !upgradeConditionMet(output.upgradeCondition, context)
        ) {
          return null;
        }

        return {
          output,
          prophecy,
        };
      });
    })
    .filter(nonNull);

  const prophecyOutputByMultiplier = prophecy_outputs.reduce((acc, curr) => {
    invariant(
      curr.output.outputResource === 'favor',
      'Prophecy output must be favor',
    );

    const key = curr.output.multiplier
      ? `${curr.output.multiplier.resourceDivisor}-${curr.output.multiplier.resource}`
      : 'no-multiplier';

    const existing = acc.get(key);
    if (existing) {
      existing.outputRatio += curr.output.outputRatio;
    } else {
      acc.set(key, { ...curr.output });
    }
    return acc;
  }, new Map<string, AbilityOutput>());

  return (
    <MPlayerDetailsSectionWithTitle title="Prophecies">
      <div css={MExpandedPlayerSummaryStyles.sectionContent}>
        {Array.from(prophecyOutputByMultiplier.values()).map((output, i) => {
          const prophecyMultiplierType = output.multiplier?.resource;
          invariant(
            prophecyMultiplierType,
            'expected prophecy multiplier type',
          );
          const prophecySymbol = nullthrows(
            AffinityOrTypeCharToProphecySymbolImage[prophecyMultiplierType],
          );
          return (
            <div key={i} css={MExpandedPlayerSummaryStyles.prophecyContainer}>
              {times(output.outputRatio, () => (
                <SymbolView
                  symbol={prophecySymbol}
                  css={MExpandedPlayerSummaryStyles.prophecySymbol}
                />
              ))}
            </div>
          );
        })}
      </div>
    </MPlayerDetailsSectionWithTitle>
  );
};

const MAvailableUpgradesView = (props: {
  player: GamePlayerViewModel;
  game: GameViewModel;
}) => {
  const { player, game } = props;

  const upgradeInfo = computeUpgradeInfo(player, game);

  const cardIDToUpgradeInfo = upgradeInfo.cardIDToLockedUpgradeInfo;

  const upgradeConditionAbilityPairs = Array.from(
    cardIDToUpgradeInfo.values(),
  ).flatMap((upgradeInfo) => {
    return upgradeInfo.abilityAndConditionPairs;
  });

  return (
    <MPlayerDetailsSectionWithTitle title="Upgrades">
      <div css={MExpandedPlayerSummaryStyles.sectionContent}>
        {upgradeConditionAbilityPairs.map(([condition, ability], i) => {
          return (
            <div key={i} css={MExpandedPlayerSummaryStyles.upgradeContainer}>
              <UpgradeCondition
                css={MExpandedPlayerSummaryStyles.upgradeCondition}
                upgradeCondition={condition}
              />
              <CardEffect
                ability={ability}
                cardType={CardAffinity.Leader}
                upgradeCondition={condition}
              />
            </div>
          );
        })}
      </div>
    </MPlayerDetailsSectionWithTitle>
  );
};

const MExpandedPlayerSummaryStyles = {
  sectionContent: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 2,
    minHeight: 20,
  }),

  favorDetailSection: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,

    padding: 2,
    paddingLeft: 4,
    zoom: 1.5,
  }),
  favorDetailText: css({
    fontSize: 12,
    color: UIColorPalette.mPlayerSummaryText,
  }),
  favorDetailSymbol: css({
    height: 12,
    width: 12,
    filter: 'grayscale(1) invert(80%)',
  }),

  prophecyContainer: css({
    color: UIColorPalette.mPlayerSummaryText,
    borderRadius: 4,
    padding: 4,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  prophecySymbol: css({
    height: 20,
    width: 'auto',
  }),

  upgradeContainer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    zoom: 2,

    backgroundColor: UIColorPalette.mPlayerDetailsUpgradeContainerBG,
    borderRadius: 4,
    padding: 2,
    paddingLeft: 3,
    paddingRight: 3,
  }),
  upgradeCondition: css({
    border: '0.5px solid grey',
    zoom: 1.5,
    backgroundColor: UIColorPalette.mPlayerDetailsUpgradeBG,
    boxSizing: 'border-box',
  }),
};
